// src/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: 'https://traderjournal-backend.onrender.com',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Exponential backoff retry logic for rate limiting
const exponentialBackoff = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (error.response?.status === 429 && retries > 0) {
      // For auth endpoints, use longer delays to avoid hitting rate limits again
      const isAuthEndpoint = error.config?.url?.includes('/api/auth/');
      const backoffDelay = isAuthEndpoint ? delay * 3 : delay; // 3x delay for auth endpoints
      
      console.log(`Rate limited (${error.config?.url}), retrying in ${backoffDelay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
      return exponentialBackoff(fn, retries - 1, backoffDelay * 2);
    }
    throw error;
  }
};

// Wrapper function for API calls with retry logic
const apiWithRetry = {
  get: (url, config) => exponentialBackoff(() => api.get(url, config)),
  post: (url, data, config) => exponentialBackoff(() => api.post(url, data, config)),
  put: (url, data, config) => exponentialBackoff(() => api.put(url, data, config)),
  delete: (url, config) => exponentialBackoff(() => api.delete(url, config)),
};

// Global request throttling to prevent rapid consecutive requests
const requestThrottle = new Map();
const rateLimitCounter = new Map();
const REQUEST_THROTTLE_TIME = 1000; // Increased to 1 second between requests
const MAX_RATE_LIMIT_RETRIES = 3;
const MAX_CONCURRENT_REQUESTS = 3; // Reduced from 5 to prevent flooding
let requestCount = 0;
let activeRequests = 0;

// Network status tracking
let isOnline = navigator.onLine;
window.addEventListener('online', () => { isOnline = true; });
window.addEventListener('offline', () => { isOnline = false; });

// Request interceptor to add token and throttle requests
api.interceptors.request.use(
  async (config) => {
    // Check network status first
    if (!isOnline) {
      console.warn('🌐 Network offline, blocking request to:', config.url);
      throw new Error('Network is offline. Please check your connection.');
    }

    // Look for accessToken (not just 'token')
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Reduce logging frequency to prevent spam
      requestCount++;
      if (requestCount % 20 === 1) { // Log every 20th request instead of 10th
        console.log(`API Request ${requestCount} with token:`, token.substring(0, 20) + '...');
      }
    } else {
      console.log('API Request without token');
    }

    // Global throttling for all endpoints to prevent flooding
    const endpointKey = config.url;
    const lastRequest = requestThrottle.get(endpointKey);
    const now = Date.now();
    
    // Check if this endpoint has been rate limited too many times recently
    const rateLimitCount = rateLimitCounter.get(endpointKey) || 0;
    if (rateLimitCount >= MAX_RATE_LIMIT_RETRIES) {
      const timeSinceLastRateLimit = now - (requestThrottle.get(`${endpointKey}_rate_limited`) || 0);
      if (timeSinceLastRateLimit < 120000) { // Increased to 2 minutes before trying again
        console.warn(`🚫 Circuit breaker: ${endpointKey} rate limited too many times, blocking for 2 minutes`);
        throw new Error(`Endpoint ${endpointKey} temporarily blocked due to rate limiting`);
      } else {
        // Reset the counter after the cooldown period
        rateLimitCounter.set(endpointKey, 0);
      }
    }
    
    if (lastRequest && (now - lastRequest) < REQUEST_THROTTLE_TIME) {
      const waitTime = REQUEST_THROTTLE_TIME - (now - lastRequest);
      console.log(`🕐 Throttling ${endpointKey}, waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    // Limit concurrent requests to prevent flooding
    const maxWaitTime = 30000; // 30 second timeout
    const startTime = Date.now();
    
    while (activeRequests >= MAX_CONCURRENT_REQUESTS) {
      if (Date.now() - startTime > maxWaitTime) {
        console.error('Request queue timeout - proceeding anyway');
        break;
      }
      console.log(`🚦 Max concurrent requests reached (${activeRequests}), waiting...`);
      await new Promise(resolve => setTimeout(resolve, 100)); // Increased wait time to 100ms
    }
    
    activeRequests++;
    requestThrottle.set(endpointKey, Date.now());
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    // Reset rate limit counter on successful response
    const endpointKey = response.config?.url;
    if (endpointKey && rateLimitCounter.has(endpointKey)) {
      rateLimitCounter.set(endpointKey, 0);
    }
    
    // Decrement active requests counter
    activeRequests = Math.max(0, activeRequests - 1);
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Track rate limiting for circuit breaker
    if (error.response?.status === 429) {
      const endpointKey = originalRequest?.url;
      if (endpointKey) {
        const currentCount = rateLimitCounter.get(endpointKey) || 0;
        rateLimitCounter.set(endpointKey, currentCount + 1);
        requestThrottle.set(`${endpointKey}_rate_limited`, Date.now());
        console.warn(`⚠️ Rate limited: ${endpointKey} (count: ${currentCount + 1})`);
      }
    }
    
    // Decrement active requests counter on error too
    activeRequests = Math.max(0, activeRequests - 1);
    
    // If we get 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        console.log('Attempting token refresh...');
        
        // Create a new axios instance to avoid interceptor loops
        const refreshResponse = await axios.post(
          'https://traderjournal-backend.onrender.com/api/auth/refresh-token',
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 10000,
          }
        );
        
        const { accessToken } = refreshResponse.data;
        
        if (accessToken) {
          // Store the new token
          localStorage.setItem('accessToken', accessToken);
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          
          console.log('Token refreshed successfully');
          
          // Retry the original request
          return api(originalRequest);
        } else {
          throw new Error('No access token received from refresh');
        }
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        
        // Clear all tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        
        // Only redirect if we're not already on the login page and register page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          console.log('Token refresh failed, redirecting to login...');
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // For other errors, just pass them through
    return Promise.reject(error);
  }
);

// Export both the original api and the retry-enabled version
export default api;
export { apiWithRetry };
